// Global Instructions Rule Applied!
//
// Security form HTTP calls. One read: GET /securities/:id →
// view__security_detail (identity + key terms + per-tranche holdings),
// validated against the strict security detail model before it reaches the
// component. One write: POST /securities/:id → at__update_security (the
// editable fields; issuer is immutable).
import { API_ROUTES, buildFullUrl, getBackendBaseUrl } from '../../../core/utils/endpoints.ts'
import {
  SecurityDetailPayload,
  validateSecurityDetailPayload
} from '../../../state_manager/data_models/security_detail_model.ts'

const BACKEND_BASE_URL = getBackendBaseUrl()

const authHeaders = (accessToken: string | null): Record<string, string> => ({
  'Content-Type': 'application/json',
  ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {})
})

export const fetchSecurityDetail = async (
  accessToken: string | null,
  securityId: string
): Promise<{ data: SecurityDetailPayload | null; error?: string }> => {
  try {
    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.ASSET_TRANSACTIONS.SECURITY_DETAIL, { id: securityId }, {}),
      {
        method: API_ROUTES.ASSET_TRANSACTIONS.SECURITY_DETAIL.method,
        headers: authHeaders(accessToken)
      }
    )
    if (!response.ok) {
      const body = await response.json().catch(() => ({}))
      throw new Error(body.error || `Security request failed (${response.status})`)
    }
    const body = await response.json()
    const payload = validateSecurityDetailPayload(body.data || {})
    return { data: payload }
  } catch (e) {
    console.error('fetchSecurityDetail failed:', e)
    return { data: null, error: e instanceof Error ? e.message : String(e) }
  }
}

// PARTIAL by design: at__update_security writes only the keys present in the
// payload, so callers send just the fields they mean to change (a key with
// null clears that field; an absent key keeps the stored value). Two editors
// share the securities row — a full-row overwrite let a stale editor revert
// the other's save.
export interface SaveSecurityPayload {
  name?: string
  type_id?: number
  valuation_type_id?: number
  currency_id?: string
  issue_price?: number | null
  nominal_value?: number | null
  interest_rate?: number | null
  interest_rate_type_id?: number | null
  compounding_frequency_id?: number | null
  day_count_convention_id?: number | null
  maturity_date?: string | null
  maturity_months?: number | null
  valuation_cap_type_id?: number | null
  valuation_cap?: number | null
  valuation_floor?: number | null
  conversion_discount?: number | null
  strike_price?: number | null
  // Per-unit fully-diluted common equivalent — defaults from the security
  // type; NULL excludes the security from FD-equivalent holdings.
  fd_common_equivalent?: number | null
  notes?: string | null
  // interest terms (docs/INTEREST_ACCRUAL_DESIGN.md section 3.3)
  first_period_end_date?: string | null
  accrues_after_maturity?: boolean
  interest_accrues_from?: string | null
  interest_payment_type_id?: number | null
  // FULL replacement set when present: rows with an id are updated, rows
  // without one created, the security's rows not in the set trashed
  rate_periods?: SaveSecurityRatePeriod[]
}

export interface SaveSecurityRatePeriod {
  id?: string | null
  effective_from: string
  interest_rate: number
  day_count_convention_id?: number | null
}

export const deleteSecurity = async (
  accessToken: string | null,
  securityId: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.ASSET_TRANSACTIONS.SECURITY_DELETE, { id: securityId }, {}),
      {
        method: API_ROUTES.ASSET_TRANSACTIONS.SECURITY_DELETE.method,
        headers: authHeaders(accessToken)
      }
    )
    if (!response.ok) {
      const body = await response.json().catch(() => ({}))
      throw new Error(body.message || body.error || `Security delete failed (${response.status})`)
    }
    return { success: true }
  } catch (e) {
    console.error('deleteSecurity failed:', e)
    return { success: false, error: e instanceof Error ? e.message : String(e) }
  }
}

export const saveSecurity = async (
  accessToken: string | null,
  securityId: string,
  payload: SaveSecurityPayload
): Promise<{ success: boolean; error?: string }> => {
  try {
    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.ASSET_TRANSACTIONS.SECURITY_SAVE, { id: securityId }, {}),
      {
        method: API_ROUTES.ASSET_TRANSACTIONS.SECURITY_SAVE.method,
        headers: authHeaders(accessToken),
        body: JSON.stringify({ data: payload })
      }
    )
    if (!response.ok) {
      const body = await response.json().catch(() => ({}))
      throw new Error(body.message || body.error || `Security save failed (${response.status})`)
    }
    return { success: true }
  } catch (e) {
    console.error('saveSecurity failed:', e)
    return { success: false, error: e instanceof Error ? e.message : String(e) }
  }
}
